import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * TailwindCSS 클래스들을 병합하는 유틸리티 함수
 * clsx로 조건부 클래스를 처리하고, twMerge로 중복 클래스를 제거합니다.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * 전화번호에 하이픈을 추가하여 포맷팅하는 함수
 */
export function formatPhoneNumber(phoneNumber: string): string {
  const cleaned = phoneNumber.replace(/\D/g, '');
  if (cleaned.length === 11) {
    return cleaned.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
  }
  return phoneNumber;
}

/**
 * 한글 키보드로 입력된 영어를 영어로 변환하는 함수
 * QR 스캐너에서 한글 모드로 스캔된 경우를 처리
 */
export function convertKoreanToEnglish(text: string): string {
  // 완성형 한글 → 영어 직접 매핑 (QR 스캔 시 자주 나타나는 패턴)
  const directMapping: { [key: string]: string } = {
    // QR JSON에서 자주 나타나는 단어들의 한글 변환 패턴
    're햔tra샤on': 'registration',
    '햔tra샤on': 'gistration', 
    'tra샤on': 'tration',
    '샤on': 'tion',
    '쇼pe': 'type',
    'e토ibi샤on': 'exhibition',
    '토ibi샤on': 'xhibition',
    'ibi샤on': 'ibition',
    // 개별 완성형 한글 변환
    '햔': 'gis',
    '샤': 'ti', 
    '쇼': 'ty',
    '토': 'xh',
    '론': 'ron',
    '션': 'tion'
  };

  // 한글 키보드 매핑 (자모 단위)
  const koreanToEnglish: { [key: string]: string } = {
    // 자음
    'ㄱ': 'r', 'ㄲ': 'R', 'ㄴ': 's', 'ㄷ': 'e', 'ㄸ': 'E', 'ㄹ': 'f', 'ㅁ': 'a', 'ㅂ': 'q', 'ㅃ': 'Q',
    'ㅅ': 't', 'ㅆ': 'T', 'ㅇ': 'd', 'ㅈ': 'w', 'ㅉ': 'W', 'ㅊ': 'c', 'ㅋ': 'z', 'ㅌ': 'x', 'ㅍ': 'v', 'ㅎ': 'g',
    // 모음
    'ㅏ': 'k', 'ㅐ': 'o', 'ㅑ': 'i', 'ㅒ': 'O', 'ㅓ': 'j', 'ㅔ': 'p', 'ㅕ': 'u', 'ㅖ': 'P', 'ㅗ': 'h',
    'ㅘ': 'hk', 'ㅙ': 'ho', 'ㅚ': 'hl', 'ㅛ': 'y', 'ㅜ': 'n', 'ㅝ': 'nj', 'ㅞ': 'np', 'ㅟ': 'nl', 'ㅠ': 'b', 'ㅡ': 'm', 'ㅢ': 'ml', 'ㅣ': 'l',
    // 특수문자
    '_': '_', '-': '-', '.': '.', ':': ':', '"': '"', '{': '{', '}': '}', '[': '[', ']': ']',
    '0': '0', '1': '1', '2': '2', '3': '3', '4': '4', '5': '5', '6': '6', '7': '7', '8': '8', '9': '9'
  };

  // 1단계: 먼저 직접 매핑으로 긴 패턴 변환 (더 정확한 변환을 위해)
  let result = text;
  const sortedKeys = Object.keys(directMapping).sort((a, b) => b.length - a.length);
  
  for (const koreanPattern of sortedKeys) {
    const englishPattern = directMapping[koreanPattern];
    result = result.replace(new RegExp(koreanPattern, 'g'), englishPattern);
  }
  
  // 2단계: 남은 한글 자모를 개별 변환
  let finalResult = '';
  for (const char of result) {
    finalResult += koreanToEnglish[char] || char;
  }
  
  return finalResult;
}

/**
 * 중복된 문자를 제거하고 올바른 단어로 교정하는 함수
 * UUID 패턴은 보호하여 잘리지 않도록 처리
 */
function fixDuplicateChars(text: string): string {
  // 자주 발생하는 중복 패턴을 올바른 단어로 교정
  const corrections: { [key: string]: string } = {
    // registration 관련 (중복)
    'regsstrratioon_id': 'registration_id',
    'regsstrratioon': 'registration',
    'regisstratioon': 'registration',
    'registratioon': 'registration',
    'regsstration': 'registration',
    
    // registration 관련 (누락)
    'regstration_id': 'registration_id',
    'regstration': 'registration',
    'reistration_id': 'registration_id',
    'reistration': 'registration',
    'registraton_id': 'registration_id',
    'registraton': 'registration',
    'registation_id': 'registration_id',
    'registation': 'registration',
    'regisration_id': 'registration_id',
    'regisration': 'registration',
    
    // exhibition 관련
    'exhhibition': 'exhibition',
    'exhibbitioon': 'exhibition',
    'exhibitioon': 'exhibition',
    'exhhibitioon': 'exhibition',
    
    // registration (단어 끝) 관련
    'registtration': 'registration',
    'registratiion': 'registration',
    'registraation': 'registration',
    'registrattion': 'registration',
    
    // type 관련
    'typpe': 'type',
    'ttype': 'type',
    'tyype': 'type'
  };

  let result = text;
  
  // 직접 매핑된 교정 먼저 적용
  for (const [wrong, correct] of Object.entries(corrections)) {
    result = result.replace(new RegExp(wrong, 'g'), correct);
  }
  
  // UUID 패턴 감지 및 보호 (정확한 UUID 형식만 매칭)
  const uuidPattern = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g;
  const placeholderMap: Map<string, string> = new Map();
  
  // registration_id 같은 일반 문자열은 UUID 보호에서 제외
  if (text.length <= 20 || !text.includes('-')) {
    console.log('🔧 [fixDuplicateChars] 짧은 문자열 또는 하이픈 없음, UUID 보호 건너뛰기:', text);
  } else {
    // UUID를 임시 플레이스홀더로 대체하여 보호 (중복 문자가 없는 안전한 형태)
    let placeholderIndex = 0;
    result = result.replace(uuidPattern, (match) => {
      const placeholder = `XUUID${placeholderIndex}XUUID`; // 중복 문자 없는 안전한 형태
      placeholderMap.set(placeholder, match);
      placeholderIndex++;
      console.log('🔧 [fixDuplicateChars] UUID 보호:', match, '→', placeholder);
      return placeholder;
    });
  }
  
  // UUID가 보호된 상태에서 일반적인 중복 문자 패턴 제거 (2-3개 연속 중복)
  result = result.replace(/(.)\1{1,2}/g, '$1');
  
  // UUID 플레이스홀더를 원래 값으로 복원
  placeholderMap.forEach((originalUuid, placeholder) => {
    result = result.replace(new RegExp(placeholder, 'g'), originalUuid);
  });
  
  console.log('🔧 [fixDuplicateChars] 중복 제거:', text, '→', result);
  return result;
}

/**
 * QR 코드 데이터를 정리하는 함수
 * 한글 키보드 모드로 스캔된 경우를 감지하고 변환
 */
export function cleanQRCodeData(qrData: string): string {
  try {
    console.log('🔧 [cleanQRCodeData] 입력 데이터:', qrData);
    
    let processedData = qrData;
    
    // 1단계: 한글 문자 또는 한글 키보드 모드 감지 및 변환
    const hasKoreanChars = /[ㄱ-ㅎ|ㅏ-ㅣ|가-힣]/.test(processedData);
    const hasKoreanKeyboardPatterns = processedData.includes('햔') || processedData.includes('샤') || 
                                     processedData.includes('쇼') || processedData.includes('토') ||
                                     processedData.includes('re햔tra샤on') || processedData.includes('쇼pe') ||
                                     processedData.includes('e토ibi샤on');
    
    if (hasKoreanChars || hasKoreanKeyboardPatterns) {
      console.log('🔧 [cleanQRCodeData] 한글 키보드 모드 감지, 영어로 변환 시도');
      console.log('🔧 [cleanQRCodeData] hasKoreanChars:', hasKoreanChars);
      console.log('🔧 [cleanQRCodeData] hasKoreanKeyboardPatterns:', hasKoreanKeyboardPatterns);
      
      // 한글을 영어로 변환
      processedData = convertKoreanToEnglish(processedData);
      console.log('🔄 [cleanQRCodeData] 한글→영어 변환 결과:', processedData);
    }
    
    // 2단계: 중복 문자 제거 및 단어 교정 (실시간 변환으로 인한 오타 수정)
    const hasDuplicates = /(.)\1{1,}/g.test(processedData) || 
                         processedData.includes('regss') || 
                         processedData.includes('exhh') ||
                         processedData.includes('registt') ||
                         processedData.includes('regstration') || // 누락 패턴
                         processedData.includes('reistration') || // 누락 패턴
                         processedData.includes('registraton') || // 누락 패턴
                         processedData.includes('registation') || // 누락 패턴
                         processedData.includes('regisration'); // 누락 패턴
    
    if (hasDuplicates) {
      console.log('🔧 [cleanQRCodeData] 중복 문자 감지, 교정 시작');
      processedData = fixDuplicateChars(processedData);
      console.log('🔄 [cleanQRCodeData] 교정 완료:', processedData);
    }
    
    console.log('🔧 [cleanQRCodeData] 최종 결과:', processedData);
    return processedData;
  } catch (error) {
    console.error('❌ [cleanQRCodeData] QR 데이터 정리 실패:', error);
    return qrData;
  }
}

/**
 * 전화번호에서 하이픈 제거 함수
 */
export function removeHyphens(phoneNumber: string): string {
  return phoneNumber.replace(/-/g, '');
}

/**
 * 날짜 포맷팅 함수 (2024년 12월 25일 오후 2:30)
 */
export function formatDate(date: Date): string {
  return new Intl.DateTimeFormat('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).format(date);
}

/**
 * 성별 표시 함수
 */
export function getGenderLabel(gender: string): string {
  switch (gender) {
    case 'male':
      return '남성';
    case 'female':
      return '여성';
    default:
      return '알 수 없음';
  }
}

/**
 * 연령대 표시 함수
 */
export function getAgeGroupLabel(ageGroup: string): string {
  switch (ageGroup) {
    case '10s':
      return '10대';
    case '20s':
      return '20대';
    case '30s':
      return '30대';
    case '40s':
      return '40대';
    case '50s':
      return '50대';
    case '60s_plus':
      return '60대 이상';
    default:
      return '알 수 없음';
  }
}

/**
 * 지역 표시 함수
 */
export function getRegionLabel(region: string): string {
  switch (region) {
    case 'seoul':
      return '서울';
    case 'gyeonggi':
      return '경기';
    case 'incheon':
      return '인천';
    case 'other':
      return '기타';
    default:
      return '알 수 없음';
  }
} 